import { useNavigate } from "react-router-dom";
import { ArrowRight, Workflow } from "lucide-react";

const FEATURES = [
  { label: "Structured Requirements", desc: "Capture, version, and validate requirements with a disciplined intake process — not freeform notes." },
  { label: "Compliance by Design", desc: "Every requirement is checked against regulatory rules before it leaves your desk." },
  { label: "Delivery Tracking", desc: "Kanban, timeline, sprint velocity, and hour logs — the full BA-to-delivery chain in one place." },
  { label: "Stakeholder & Team Management", desc: "Document who owns what, track capacity, and keep everyone aligned from day one." },
];

export default function BAIntroPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col md:flex-row overflow-hidden">

      {/* ── Left panel ── */}
      <div className="flex-1 flex flex-col justify-between p-6 sm:p-10 lg:p-14 bg-[#0d1b3e]">
        <div className="flex items-center gap-2.5">
          <Workflow className="h-5 w-5 text-[#7eaaff]" />
          <span className="text-xs uppercase tracking-widest text-white/40 font-mono">BA Workbench</span>
        </div>

        <div className="max-w-md">
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-6 text-white"
            style={{ fontFamily: "Georgia, serif" }}
          >
            A workflow built<br />for Business<br />Analysts.
          </h1>
          <p className="text-white/55 leading-relaxed mb-10 font-mono text-sm">
            Not a chatbot. Not an AI toy. A disciplined BA workbench built around
            the structured processes that actually get software delivered — correctly,
            compliantly, and on time.
          </p>

          <div className="space-y-5 mb-12">
            {FEATURES.map((f) => (
              <div key={f.label} className="flex gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#7eaaff]/60 shrink-0 mt-2" />
                <div>
                  <p className="text-white/90 text-sm font-semibold">{f.label}</p>
                  <p className="text-white/40 text-xs mt-0.5 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => navigate("/studio")}
            className="group inline-flex items-center gap-3 bg-[#2d5bbf] hover:bg-[#3a6fd4] text-white px-7 py-3.5 text-sm font-semibold tracking-wide transition-all duration-200 rounded-sm"
          >
            Launch Studio
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        <p className="text-xs text-white/20 font-mono">
          Structured process · BA owns every decision · Data stays local
        </p>
      </div>

      {/* ── Right panel — morphing blobs ── */}
      <div className="hidden md:flex w-full md:w-[46%] shrink-0 bg-[#f0f4ff] items-center justify-center relative overflow-hidden min-h-[300px]">

        {/* Background blob — largest, slowest */}
        <div
          className="absolute w-[260px] h-[260px] sm:w-[360px] sm:h-[360px] lg:w-[420px] lg:h-[420px] bg-[#c7d9ff] opacity-60 animate-morph-slow"
          style={{ top: "50%", left: "50%", transform: "translate(-60%, -40%)" }}
        />

        {/* Mid blob */}
        <div
          className="absolute w-[180px] h-[180px] sm:w-[240px] sm:h-[240px] lg:w-[300px] lg:h-[300px] bg-[#a8c4f0] opacity-70 animate-morph"
          style={{ top: "50%", left: "50%", transform: "translate(-40%, -55%)" }}
        />

        {/* Accent blob — smallest, offset timing */}
        <div
          className="absolute w-[180px] h-[180px] bg-[#2d5bbf] opacity-25 animate-morph-offset"
          style={{ top: "50%", left: "50%", transform: "translate(-20%, -30%)" }}
        />

        {/* Floating labels */}
        {[
          { text: "Requirements",  delay: "0s",   top: "22%", left: "18%" },
          { text: "Compliance",    delay: "1.5s", top: "32%", left: "58%" },
          { text: "Stakeholders",  delay: "0.8s", top: "50%", left: "14%" },
          { text: "Delivery",      delay: "2s",   top: "62%", left: "55%" },
          { text: "Process First", delay: "1.2s", top: "74%", left: "30%" },
        ].map((item) => (
          <div
            key={item.text}
            className="absolute z-10 px-3 py-1.5 rounded-full bg-white/80 border border-[#a8c4f0] text-[#1e3a6e] text-xs font-semibold shadow-sm backdrop-blur-sm select-none pointer-events-none"
            style={{
              top: item.top,
              left: item.left,
              animation: `fade-in 0.6s ease-out ${item.delay} both`,
            }}
          >
            {item.text}
          </div>
        ))}
      </div>
    </div>
  );
}
